import { useEffect, useState } from 'react';

import { Wordmark } from './Wordmark';
import { brand } from '../../config/brand';

/**
 * The boot screen.
 *
 * ## When this is on screen
 *
 * Exactly once per page load, while the session question is being answered. The
 * access token lives in memory only (§7.1), so every load starts signed out and
 * exchanges the refresh cookie for a new one before anything can render - see
 * the comment in `App.tsx` for why rendering routes during that round trip is
 * worse than waiting.
 *
 * That wait is usually 100-300ms, which is why the whole thing is behind the
 * 400ms rule: on a warm connection nobody ever sees this, and that is correct.
 * It exists for the cold load, the slow network and the first visit, where the
 * alternative is a black rectangle.
 *
 * ## The status lines are real
 *
 * The design's mockup cycles through four of them, one of which is "412 people
 * already listening". Nothing here knows that number, and a fabricated one on
 * the first screen of the site sets exactly the wrong expectation about every
 * other number on it - so the lines that survived are the ones that describe
 * what the application is genuinely doing while they are on screen, and they
 * are phrased so as not to imply measurement.
 */
const LINES = [
  'warming up the decks',
  'finding your session',
  "finding artists nobody's found yet",
  "loading tonight's queue",
];

/** How long each line holds before the next fades in. */
const PER_LINE = 2.4;

export function Splash() {
  /*
   * Behind the 400ms rule, like everything else - but implemented here rather
   * than with `Deferred`, because this component *is* the whole frame. A
   * wrapper would leave `App` rendering an empty div, and the point is that
   * nothing is painted at all until the wait is worth acknowledging.
   */
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShown(true), 400);

    return () => clearTimeout(timer);
  }, []);

  if (!shown) return <div className="min-h-full bg-app" />;

  return (
    <div
      role="status"
      aria-label={`Loading ${brand.name}`}
      data-gv-loader="splash"
      data-splash
      className="relative flex min-h-full items-center justify-center overflow-hidden bg-app px-10 py-20"
      style={{
        background: 'radial-gradient(120% 90% at 50% 8%,var(--color-splash-top) 0%,var(--color-splash-mid) 46%,var(--color-void) 100%)',
      }}
    >
      {/* Two rings on a half-cycle offset, so one is always expanding. A single
          ring reads as a repeating blip; two read as a pulse. */}
      {[0, 1.7].map((delay) => (
        <span
          key={delay}
          data-gv-still
          aria-hidden="true"
          className="absolute h-[520px] w-[520px] rounded-[var(--radius-pill)] border border-ring-lit"
          style={{ animation: `gv-ring 3.4s cubic-bezier(0.22,0.68,0.24,1) ${delay}s infinite` }}
        />
      ))}

      <div className="relative flex max-w-xl flex-col items-center gap-11">
        <Mark />

        <div className="flex flex-col items-center gap-[18px]">
          {/*
            The logo file, not a text wordmark.
            `config/brand.ts` is the only place in the SPA allowed to know the
            product's name, and the two-tone treatment lives inside the SVG - so
            spelling it out here in two coloured spans would both break that rule
            and fork the styling.
          */}
          <span
            data-gv-still
            className="inline-flex"
            style={{ animation: 'gv-drift 4s ease-in-out infinite' }}
          >
            <Wordmark className="h-9 w-auto" />
          </span>

          {/*
            Absolutely positioned and stacked, so the lines cross-fade in place.
            Laid out in flow they would each take a turn shifting the block
            below them by however much their width differs.
          */}
          <div className="relative flex h-[22px] w-[340px] items-center justify-center">
            {LINES.map((text, i) => (
              <span
                key={text}
                data-gv-still
                aria-hidden="true"
                className="absolute whitespace-nowrap font-mono text-xs uppercase tracking-[0.12em] text-quiet opacity-0"
                style={{
                  animation: `gv-fade-cycle ${(LINES.length * PER_LINE).toFixed(1)}s ease-in-out ${(
                    i * PER_LINE
                  ).toFixed(1)}s infinite`,
                }}
              >
                {text}
              </span>
            ))}
          </div>
        </div>

        <div className="flex w-[340px] flex-col gap-3">
          <div className="relative h-[3px] overflow-hidden rounded-[var(--radius-pill)] bg-track-dim">
            <span
              data-gv-still
              aria-hidden="true"
              className="absolute inset-y-0 w-[34%] rounded-[var(--radius-pill)]"
              style={{
                background: 'linear-gradient(90deg,var(--color-signal),var(--color-hot),var(--color-fresh))',
                animation: 'gv-sweep 1.9s cubic-bezier(0.55,0.1,0.4,0.9) infinite',
              }}
            />
          </div>
          <div className="flex justify-between font-mono text-[10px] text-quieter">
            <span>tuning the room</span>
            {/* Not a countdown and not a percentage: nothing here can measure
                either, and a number that turns out to be wrong on a slow
                connection is worse than an honest hedge. */}
            <span>usually under 2s</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The logo tile: five bars behind a slow sweep.
 *
 * The same five-colour spectrum as `Loader`, at the size where the gradient
 * across them actually reads. This is the one place the brand mark is animated,
 * which is what makes the boot screen feel like the product rather than like a
 * loading page that happens to have a logo on it.
 */
function Mark() {
  const colors = ['var(--color-signal)', 'var(--color-violet)', 'var(--color-hot)', 'var(--color-teal)', 'var(--color-fresh)'];

  return (
    <div
      data-gv-still
      aria-hidden="true"
      className="relative flex h-[172px] w-[172px] items-center justify-center gap-[11px] rounded-[46px] border border-frame p-[38px]"
      style={{
        background: 'linear-gradient(155deg,var(--color-tile-top) 0%,var(--color-tile-bottom) 100%)',
        boxShadow: '0 40px 90px -40px var(--color-signal-glow)',
        animation: 'gv-pulse 2.6s ease-in-out infinite',
      }}
    >
      {colors.map((color, i) => (
        <span
          key={color}
          data-gv-bar
          className="h-full w-[13px] rounded-[var(--radius-pill)]"
          style={{
            background: color,
            transformOrigin: 'center',
            animation: `gv-bar 1.15s cubic-bezier(0.42,0,0.58,1) ${(i * 0.13).toFixed(2)}s infinite`,
          }}
        />
      ))}

      {/* A light passing over the tile. Clipped to the tile's own radius, and
          non-interactive, so it never eats a click. */}
      <span className="pointer-events-none absolute inset-0 overflow-hidden rounded-[46px]">
        <span
          data-gv-still
          className="absolute inset-y-0 w-[36%]"
          style={{
            background:
              'linear-gradient(90deg,var(--color-signal-clear) 0%,var(--color-signal-sweep) 50%,var(--color-signal-clear) 100%)',
            animation: 'gv-sweep 2.8s linear infinite',
          }}
        />
      </span>
    </div>
  );
}
